/**
 * One line across the top of the app, from whoever runs it.
 *
 * The switches on the status page can pause uploads or voice, but nothing
 * could say why. So a maintenance window looked like this from the outside:
 * the thing you were doing stopped working, no message, and the only way to
 * find out whether it was you or the server was to ask somebody. People
 * assume it is their own connection and reinstall, or assume it is broken
 * for good and leave.
 *
 * It comes from the health check rather than anywhere that needs a session,
 * because the person who cannot sign in is exactly who needs to be told the
 * server is down. It is plain text and nothing else - a banner rendered from
 * admin input at the top of every page is the last place to be clever about
 * what it may contain.
 *
 * Dismissing it hides that exact message for this browser only. Change the
 * wording and it comes back, which is what you want: a new notice is news.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

type Notice = { message: string; level: 'info' | 'warn' | 'bad'; until: number | null };

const SEEN = 'pugo.noticeSeen';

/** A short fingerprint of the wording, so "dismissed" follows the message
    rather than the fact that any notice exists. */
function keyOf(n: Notice): string {
  return n.level + ':' + n.message;
}

function left(until: number): string {
  const mins = Math.round((until - Date.now()) / 60000);
  if (mins <= 0) return '';
  if (mins < 60) return 'about ' + mins + ' minute' + (mins === 1 ? '' : 's');
  const hrs = Math.round(mins / 60);
  return 'about ' + hrs + ' hour' + (hrs === 1 ? '' : 's');
}

export function ServerNotice() {
  const [hidden, setHidden] = useState<string | null>(() => {
    try { return localStorage.getItem(SEEN); } catch { return null; }
  });

  /*
   * Asked every couple of minutes, not every couple of seconds.
   *
   * A notice is set by hand and read by eye; polling it hard would put the
   * whole userbase on the health check for a field that is null almost
   * always. Two minutes is soon enough for "back in twenty".
   */
  const q = useQuery({
    queryKey: ['server', 'notice'],
    queryFn: () => api.get<{ notice: Notice | null }>('/api/health'),
    refetchInterval: 120_000,
    staleTime: 60_000
  });

  const notice = q.data?.notice ?? null;

  /* An expiring notice should go on its own without a reload, and the only
     thing that has to happen for that is a re-render at the right moment. */
  const [, tick] = useState(0);
  useEffect(() => {
    if (!notice?.until) return;
    const ms = notice.until - Date.now();
    if (ms <= 0 || ms > 2 ** 31 - 1) return;
    const t = setTimeout(() => tick((n) => n + 1), ms + 500);
    return () => clearTimeout(t);
  }, [notice?.until]);

  if (!notice || !notice.message) return null;
  if (notice.until && notice.until <= Date.now()) return null;
  if (hidden === keyOf(notice)) return null;

  const tone = notice.level === 'bad' ? 'var(--bad)' : notice.level === 'warn' ? 'var(--warn)' : 'var(--accent)';
  const ends = notice.until ? left(notice.until) : '';

  return (
    <div
      role="status"
      data-chrome="notice"
      className={cn(
        'flex flex-none items-center gap-2.5 px-4 py-2 text-[13px]',
        /* Entrance only, and backwards rather than both: a fill-mode of both
           leaves an identity transform behind for ever, and any transform
           makes this a containing block for every fixed thing under it. */
        'animate-[noticeIn_.32s_var(--ease-out)_backwards] motion-reduce:animate-none'
      )}
      style={{
        background: 'color-mix(in srgb, ' + tone + ' 15%, var(--bg-1))',
        boxShadow: 'inset 0 -1px 0 color-mix(in srgb, ' + tone + ' 35%, transparent)',
        color: 'var(--ink)'
      }}
    >
      <Icon
        name={notice.level === 'info' ? 'info' : 'alert'}
        size={15}
        className="flex-none"
        style={{ color: tone }}
      />
      <span className="min-w-0 flex-1 truncate font-semibold" title={notice.message}>
        {notice.message}
      </span>
      {ends ? <span className="flex-none text-[12px] text-mute">for {ends}</span> : null}
      <button
        type="button"
        aria-label="Hide this notice"
        onClick={() => {
          const k = keyOf(notice);
          setHidden(k);
          try { localStorage.setItem(SEEN, k); } catch { /* private window */ }
        }}
        className="flex-none rounded-[6px] p-1 text-mute outline-none transition-colors duration-[var(--t-hover)] hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
